import { cloneDeep } from 'lodash';
import { compressToBase64 } from 'lz-string';
import { useState } from 'react';
import { graphql, useMutation } from 'react-relay';
import config from 'src/shared/config';
import { T, toGlobalID, toLocalGuid } from 'src/shared/helpers';
import { withTimeout } from 'src/shared/withTimeout';
import { showErrorNotification } from '../../notificationToaster';
import { loadProductWithProductQuery$data } from '../loaders/__generated__/loadProductWithProductQuery.graphql';
import usePreview3DStore from '../store';
import { tryOnGraphqlMap } from '../tryOnTypes';
import { serializeComposerExpression } from '../helpers/serializeComposerExpression';
import {
    useSaveProductCreateOrUpdateSceneMutation,
    useSaveProductCreateOrUpdateSceneMutation$data
} from './__generated__/useSaveProductCreateOrUpdateSceneMutation.graphql';
import { useSaveProductOptimizedSceneMutation, useSaveProductOptimizedSceneMutation$data } from './__generated__/useSaveProductOptimizedSceneMutation.graphql';
import { TryOnType, useSaveProductCreateOrUpdateVTOMutation } from './__generated__/useSaveProductCreateOrUpdateVTOMutation.graphql';
import { useSaveProductDisableVTOMutation } from './__generated__/useSaveProductDisableVTOMutation.graphql';
import {
    ModelColorCreateInput,
    ModelUpdateOrCreateInput,
    useSaveProductUpdateProductMutation
} from './__generated__/useSaveProductUpdateProductMutation.graphql';
import {
    useSaveProductUpdateProductOnlyMutation,
    useSaveProductUpdateProductOnlyMutation$data
} from './__generated__/useSaveProductUpdateProductOnlyMutation.graphql';

const SAVE_OPERATION_TIMEOUT_MS = 120_000;

const clonePlain = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const serializeExpression = serializeComposerExpression;

const serializeSceneAction = (action: any) => ({
    actionId: action.actionID > 0 ? action.actionID : null,
    actionGuid: action.actionGuid,
    name: action.name ?? null,
    actionTypeID: action.actionTypeID ?? 0,
    sceneElementTypeID: action.sceneElementTypeID ?? 0,
    selector: action.selector ?? null,
    propertyName: action.propertyName ?? null,
    value: action.value ?? null,
    execOrder: action.execOrder ?? 0,
    enabled: action.enabled !== false,
    // The API contract is a bit mask, not an array. Older Composer payloads sent []
    // for missing values, which failed JSON deserialization on compressed saves.
    deviceTypes: typeof action.deviceTypes === 'number' ? action.deviceTypes : 0
});

// ✅ NUOVO: Funzione per validare le misurazioni AR
const validateARMeasurements = async (sceneEditor: any): Promise<boolean> => {
    try {
        // Verifica se esiste modelMeasurements con valori validi
        if (typeof sceneEditor.modelMeasurements !== 'undefined' && sceneEditor.modelMeasurements !== null) {
            const measurements = sceneEditor.modelMeasurements;
            const hasValidModelMeasurements =
                measurements &&
                (measurements.x > 0 || measurements.y > 0 || measurements.z > 0 || measurements.width > 0 || measurements.height > 0 || measurements.depth > 0);

            if (hasValidModelMeasurements) {
                return true;
            }
        }

        // Controlla le misurazioni specifiche per ogni mesh
        const meshes = sceneEditor.getSceneMeshes?.() || [];

        for (const mesh of meshes) {
            try {
                const meshId = mesh.id;

                // Controlla tutte le misurazioni specifiche come nel dialog AR
                const width = sceneEditor.getMeshWidthCm?.(meshId);
                const height = sceneEditor.getMeshHeightCm?.(meshId);
                const depth = sceneEditor.getMeshDepthCm?.(meshId);
                const generalSize = sceneEditor.getMeshSizeCm?.(meshId);

                // Se almeno una misurazione è valida (> 0), considera la mesh misurata
                if ((width && width > 0) || (height && height > 0) || (depth && depth > 0) || (generalSize && generalSize > 0)) {
                    return true;
                }
            } catch (error) {
                // Continua con la prossima mesh se c'è un errore
                continue;
            }
        }

        return false;
    } catch (error) {
        // In caso di errore nella validazione, permetti il salvataggio
        return true;
    }
};

// ✅ NUOVO: Funzione esportata per validare AR prima del salvataggio (usata dal SceneEditor)
export const validateARBeforeSave = async (sceneEditor: any): Promise<void> => {
    if (!sceneEditor) return;

    // ✅ PRIMA controlla se l'AR è globalmente abilitato nell'AR Editor
    let isGlobalAREnabled = false;
    try {
        const sceneConfig = sceneEditor.getSceneConfig();
        const generalConfig = sceneConfig?.get?.('general');
        if (generalConfig) {
            isGlobalAREnabled = generalConfig.get('arEnabled') || false;
        }
    } catch (error) {
        console.log('⚠️ AR global config not found, checking mesh-level AR...');
    }

    // Se l'AR globale non è abilitato, salta la validazione
    if (!isGlobalAREnabled) {
        console.log('ℹ️ Global AR disabled, skipping AR validation');
        return;
    }

    console.log('✅ Global AR enabled, proceeding with AR validation...');

    // Controlla se ci sono mesh con AR abilitato
    let hasAREnabled = false;
    try {
        const meshes = sceneEditor.getSceneMeshes?.() || [];

        for (const mesh of meshes) {
            try {
                const arEnabled = sceneEditor.isAREnabledForMesh?.(mesh.id) === true;
                if (arEnabled) {
                    hasAREnabled = true;
                    break;
                }
            } catch (error) {
                continue;
            }
        }
    } catch (error) {
        // In caso di errore, permetti il salvataggio
        return;
    }

    if (hasAREnabled) {
        // Controlla se ci sono misurazioni AR valide
        const hasValidARMeasurements = await validateARMeasurements(sceneEditor);

        if (!hasValidARMeasurements) {
            showErrorNotification({ message: T._('Error while saving: the AR is enabled yet there are no measurements added', 'Admin') });
            throw new Error('AR validation failed: no measurements found');
        }
    }
};

// Shared function to serialize camera-side mappings for both useSaveProduct and useSaveProductAndScene
const serializeCameraSideMappings = (colors: NonNullable<loadProductWithProductQuery$data['model']>['colors']) => {
    const mappings: { colorID: number; sideID: number; cameraLocationID: string | null }[] = [];

    for (const color of colors) {
        for (const side of color.sides) {
            // Only include mappings where cameraLocationID is set (not empty)
            const cameraLocationID = side.cameraLocationID;
            mappings.push({
                colorID: color.colorID!,
                sideID: side.sideID!,
                cameraLocationID: cameraLocationID
            });
        }
    }

    return mappings;
};

// Save just product
const useSaveProduct = () => {
    const [zkProduct, incrementSaveVersion] = usePreview3DStore((store) => [store.zkProduct, store.incrementSaveVersion]);

    /* save product */
    const [updateProduct, isSavingProduct] = useMutation<useSaveProductUpdateProductOnlyMutation>(graphql`
        mutation useSaveProductUpdateProductOnlyMutation($input: ModelUpdateOrCreateInput, $compressedInput: String) {
            createOrUpdateModelV2(input: $input, compressedInput: $compressedInput) {
                model {
                    id
                    modelID
                    salesChannelID
                    attributeGroups {
                        groupID
                        groupGuid
                        tempImageFileKey
                        tempImageUrl
                        imageUrl
                    }
                    attributeSteps {
                        stepID
                        stepGuid
                    }
                    templateGroups {
                        templateGroupID
                        templateGroupGuid
                        groupID
                        stepID
                    }
                    attributes {
                        attributeID
                        attributeGuid
                        pricings {
                            attributePricingID
                            selectedOptionGuid
                            selectedValue
                            pricingID
                        }
                        preActions {
                            actionID
                            actionGuid
                        }
                        valueActions {
                            actionID
                            actionGuid
                        }
                        postActions {
                            actionID
                            actionGuid
                        }
                        options {
                            optionID
                            optionGuid
                            tempImageFileKey
                            tempImageUrl
                            imageUrl
                            tempImageFileObjectID
                            imageFileObjectID
                            actions {
                                actionID
                                actionGuid
                            }
                        }
                    }
                    colors {
                        colorID
                        colorGuid
                        sides {
                            sideID
                            sideGuid
                            tempImageFileKey
                            imageUrl
                            areas {
                                areaID
                                guid
                            }
                        }
                    }
                }
            }
        }
    `);
    const projectMeshes = (meshSide: NonNullable<loadProductWithProductQuery$data['model']>['scenes'][0]['meshSides'][0]) => ({
        key: meshSide.key ?? null,
        sideID: meshSide.sideID,
        colorID: meshSide.colorID,
        attributeID: meshSide.attributeID ?? null,
        meshID: meshSide.meshID,
        matrix: meshSide.matrix,
        transform: meshSide.transform,
        materialID: meshSide.materialID,
        materialTexture: meshSide.materialTexture ?? null,
        isNormalmapEnabled: meshSide.isNormalmapEnabled ?? false,
        percHeight: meshSide.percHeight ?? 1,
        percWidth: meshSide.percWidth ?? 1,
        subMeshID: meshSide.subMeshID ?? 0,
        uvChannel: meshSide.uvChannel ?? 0,
        config: meshSide.config ? clonePlain(meshSide.config) : null
    });

    const buildScenePayload = (scene: NonNullable<loadProductWithProductQuery$data['model']>['scenes'][0]) => ({
        sceneId: toLocalGuid(scene.sceneID!),
        isDefault: scene.isDefault,
        meshDesignEffects: (scene.meshDesignEffects ?? []).map((effect) => ({
            effectID: effect.effectID ?? null,
            meshID: effect.meshID,
            subMeshID: effect.subMeshID ?? 0,
            name: effect.name ?? null,
            materialID: effect.materialID ?? null,
            config: effect.config ? clonePlain(effect.config) : null,
            isDefault: effect.isDefault ?? false
        })),
        meshSides: scene.meshSides.map(projectMeshes)
    });

    const serializeColors = (colors: NonNullable<loadProductWithProductQuery$data['model']>['colors']): ModelColorCreateInput[] =>
        colors.map((color) => ({
            colorId: color?.colorID ?? null,
            colorGuid: color?.colorGuid ?? '',
            name: color?.name ?? null,
            code: color?.code ?? '',
            colorProviderCode: color?.colorProviderCode ?? null,
            position: color?.position ?? 0,
            sides:
                color?.sides?.map((side) => ({
                    sideId: side?.sideID ?? null,
                    sideGuid: side?.sideGuid ?? null,
                    name: side?.name ?? null,
                    code: side?.code ?? null,
                    imageUrl: side?.imageUrl ?? null,
                    tempImageFileKey: side?.tempImageFileKey ?? '',
                    sortOrder: side?.sortOrder ?? 0,
                    mandatory: side?.mandatory ?? false,
                    canRotate: side?.canRotate ?? false,
                    ppcm: side?.ppcm ?? 0,
                    cameraLocationID: side?.cameraLocationID ?? null,
                    areas:
                        side?.areas?.map((area) => ({
                            areaId: area?.areaID ?? null,
                            guid: area?.guid ?? null,
                            name: area?.name ?? null,
                            path: area?.path ?? null,
                            pathSVG: area?.pathSVG ?? null,
                            boundsX: area?.boundsX ?? 0,
                            boundsY: area?.boundsY ?? 0,
                            boundsWidth: area?.boundsWidth ?? 0,
                            boundsHeight: area?.boundsHeight ?? 0,
                            clipOut: area?.clipOut ?? false,
                            layers:
                                area?.layers?.map((layer) => ({
                                    id: layer.layerID ? toGlobalID(layer.layerID, 'AreaLayer') : null,
                                    name: layer.name ?? '',
                                    displayName: layer.displayName ?? '',
                                    description: layer.description ?? '',
                                    displayOrder: layer.displayOrder ?? 0,
                                    isVisibleInCanvas: layer.isVisibleInCanvas ?? true,
                                    isVisibleInCart: layer.isVisibleInCart ?? true,
                                    isVisibleInOutput: layer.isVisibleInOutput ?? true,
                                    showLabel: layer.showLabel ?? true,
                                    isClipMask: layer.isClipMask ?? false,
                                    isOutputClipped: layer.isOutputClipped ?? false,
                                    showIn3DPreview: layer.showIn3DPreview ?? true,
                                    svg: layer.svg ?? '',
                                    json: layer.json ?? ''
                                })) ?? [],
                            templateFile: area?.templateFile
                                ? {
                                      fileID: area.templateFile.fileID ?? null,
                                      fileObjectID: area.templateFile.fileObjectID ?? null,
                                      documentWidth: area.templateFile.documentWidth ?? 0,
                                      documentHeight: area.templateFile.documentHeight ?? 0
                                  }
                                : null
                        })) ?? []
                })) ?? []
        }));

    const mapPreviewVariants = (preview: NonNullable<loadProductWithProductQuery$data['model']>['preview3D']) =>
        preview && preview.variantsAttributeOptions
            ? {
                  variantsAttributeOptions: {
                      attributeGuid: preview.variantsAttributeOptions.attributeGuid,
                      variableMaterials:
                          preview.variantsAttributeOptions.variableMaterials?.map((vm) => ({
                              materialID: vm?.materialID,
                              meshes: vm?.meshes
                          })) ?? [],
                      variantOptions:
                          preview.variantsAttributeOptions.variantOptions?.map((vo) => ({
                              variantID: vo?.variantID,
                              optionGuid: vo?.optionGuid,
                              meshes: vo?.meshes,
                              materials:
                                  vo?.materials?.map((mat) => ({
                                      optionMaterialID: mat?.optionMaterialID,
                                      variableMaterialID: mat?.variableMaterialID,
                                      actions:
                                          mat?.actions?.map((action) => ({
                                              actionGuid: action?.actionGuid,
                                              meshID: action?.meshID
                                          })) ?? []
                                  })) ?? []
                          })) ?? []
                  }
              }
            : null;
    const serializeAttributesStep = (steps: NonNullable<loadProductWithProductQuery$data['model']>['attributeSteps']) =>
        steps.map((step) => ({
            cameraLocationID: step.cameraLocationID ?? null,
            displayOrder: step.displayOrder ?? 0,
            enabled: step.enabled !== false,
            groupGuid: step.groupGuid,
            groupID: step.groupID ?? null,
            name: step.name ?? null,
            stepGuid: step.stepGuid,
            stepID: step.stepID ?? null
        }));

    const serializeAttributesGroup = (groups: NonNullable<loadProductWithProductQuery$data['model']>['attributeGroups']) =>
        groups.map((group) => ({
            attributesAlwaysOpened: group.attributesAlwaysOpened ?? false,
            direction: group.direction ?? 0,
            displayOrder: group.displayOrder ?? 0,
            enabled: group.enabled !== false,
            groupGuid: group.groupGuid,
            groupID: group.groupID ?? null,
            imageUrl: group.imageUrl ?? null,
            meshSelector: group.meshSelector ?? null,
            name: group.name ?? null,
            sceneCameraLocationID: group.sceneCameraLocationID ?? null,
            tempImageFileKey: group.tempImageFileKey ?? null,
            tempImageUrl: group.tempImageUrl ?? null
        }));

    const serializeTemplateGroups = (groups: NonNullable<loadProductWithProductQuery$data['model']>['templateGroups']) =>
        groups.map((group) => ({
            templateGroupID: group.templateGroupID,
            templateGroupGuid: group.templateGroupGuid,
            name: group.name ?? '',
            displayOrder: group.displayOrder,
            groupID: group.groupID,
            groupGuid: group.groupGuid,
            stepID: group.stepID,
            stepGuid: group.stepGuid,
            compositions: group.compositions ?? [],
            macrocategories: group.macrocategories ?? [],
            categories: (group.categories ?? []).flatMap((category) =>
                category ? [{ categoryID: category.categoryID, macroCategoryID: category.macroCategoryID }] : []
            )
        }));

    const serializeAttributes = (attributes: NonNullable<loadProductWithProductQuery$data['model']>['attributes']) =>
        attributes.map((attribute) => ({
            attributeId: attribute?.attributeID != null && attribute.attributeID > 0 ? attribute.attributeID : null,
            attributeGuid: attribute?.attributeGuid,
            attributeCode: attribute?.attributeCode ?? null,
            attributeTypeID: attribute?.attributeTypeID ?? 0,
            defaultValue: attribute?.defaultValue ?? null,
            displayOrder: attribute?.displayOrder ?? 0,
            enabled: attribute?.enabled !== false,
            hideOptionsLabel: attribute?.hideOptionsLabel ?? false,
            preActions: (attribute?.preActions ?? []).map(serializeSceneAction),
            postActions: (attribute?.postActions ?? []).map(serializeSceneAction),
            pricings: (attribute?.pricings ?? []).flatMap((pricing) =>
                pricing
                    ? [
                          {
                              attributePricingID: pricing.attributePricingID > 0 ? pricing.attributePricingID : null,
                              selectedOptionID: pricing.selectedOptionID ?? null,
                              selectedOptionGuid: pricing.selectedOptionGuid ?? null,
                              selectedValue: pricing.selectedValue ?? null,
                              constraintExpression: serializeExpression(pricing.constraintExpression),
                              price: pricing.price ?? 0,
                              pricingID: pricing.pricingID ?? -1,
                              name: pricing.name ?? null
                          }
                      ]
                    : []
            ),
            valueActions: (attribute?.valueActions ?? []).map(serializeSceneAction),
            name: attribute?.name ?? null,
            description: attribute?.description ?? null,
            groupID: attribute?.groupID ?? null,
            groupGuid: attribute?.groupGuid ?? null,
            stepID: attribute?.stepID ?? null,
            stepGuid: attribute?.stepGuid ?? null,
            optionPreviewShapeID: attribute?.optionPreviewShapeID ?? null,
            constraintExpression: serializeExpression(attribute?.constraintExpression),
            meshSelector: attribute?.meshSelector ?? null,
            sceneCameraLocationID: attribute?.sceneCameraLocationID ?? null,
            options:
                attribute?.options?.map((option) => ({
                    optionId: option?.optionID != null && option.optionID > 0 ? option.optionID : null,
                    optionGuid: option!.optionGuid,
                    optionCode: option!.optionCode ?? null,
                    tempImageFileKey: option!.tempImageFileKey ?? null,
                    imageUrl: option!.imageUrl ?? null,
                    name: option!.name ?? null,
                    description: option!.description ?? null,
                    displayOrder: option!.displayOrder ?? 0,
                    enabled: option!.enabled !== false,
                    isDefault: option!.isDefault ?? false,
                    tempImageFileObjectID: option!.tempImageFileObjectID ?? null,
                    imageFileObjectID: option!.imageFileObjectID ?? null,
                    constraintExpression: serializeExpression(option?.constraintExpression),
                    extensionFields:
                        option?.extensionFields?.flatMap((field) =>
                            field
                                ? [
                                      {
                                          extensionFieldID: field.extensionFieldID > 0 ? field.extensionFieldID : null,
                                          value: field.value ?? null,
                                          name: field.name ?? null,
                                          label: field.label ?? null,
                                          formatString: field.formatString ?? null,
                                          dataTypeID: field.dataTypeID ?? 0,
                                          operationTypeID: field.operationTypeID ?? 0
                                      }
                                  ]
                                : []
                        ) ?? [],
                    actions: (option?.actions ?? []).map(serializeSceneAction)
                })) ?? []
        }));

    const reconcileSavedProduct = (
        product: MPlaza.Model,
        saved: NonNullable<useSaveProductUpdateProductOnlyMutation$data['createOrUpdateModelV2']['model']>
    ) => {
        product.set('modelID', saved.modelID);

        const findBy = (collection: any, key: string, value: unknown) => collection?.find?.((item: any) => item.get?.(key) === value);
        const reconcileActions = (actions: any, savedActions: ReadonlyArray<any> | null | undefined) => {
            for (const savedAction of savedActions ?? [])
                if (savedAction) findBy(actions, 'actionGuid', savedAction.actionGuid)?.set('actionID', savedAction.actionID);
        };
        for (const savedPreset of saved.templateGroups ?? []) {
            const preset = findBy(product.get('templateGroups'), 'templateGroupGuid', savedPreset.templateGroupGuid);
            preset?.set('templateGroupID', savedPreset.templateGroupID);
            preset?.set('groupID', savedPreset.groupID);
            preset?.set('stepID', savedPreset.stepID);
        }
        for (const savedGroup of saved.attributeGroups ?? []) {
            const group = findBy(product.get('attributeGroups'), 'groupGuid', savedGroup.groupGuid);
            group?.set('groupID', savedGroup.groupID);
            group?.set('tempImageFileKey', savedGroup.tempImageFileKey ?? '');
            group?.set('tempImageUrl', savedGroup.tempImageUrl ?? '');
            group?.set('imageUrl', savedGroup.imageUrl ?? '');
        }
        for (const savedStep of saved.attributeSteps ?? []) {
            findBy(product.get('attributeSteps'), 'stepGuid', savedStep.stepGuid)?.set('stepID', savedStep.stepID);
        }
        for (const savedAttribute of saved.attributes ?? []) {
            const attribute = findBy(product.get('attributes'), 'attributeGuid', savedAttribute.attributeGuid);
            attribute?.set('attributeID', savedAttribute.attributeID);
            reconcileActions(attribute?.get('preActions'), savedAttribute.preActions);
            reconcileActions(attribute?.get('valueActions'), savedAttribute.valueActions);
            reconcileActions(attribute?.get('postActions'), savedAttribute.postActions);
            for (const savedPricing of savedAttribute.pricings ?? []) {
                if (!savedPricing) continue;
                const pricing = attribute
                    ?.get('pricings')
                    ?.find?.((item: any) =>
                        savedPricing.selectedOptionGuid
                            ? item.get?.('selectedOptionGuid') === savedPricing.selectedOptionGuid
                            : item.get?.('selectedValue') === savedPricing.selectedValue && item.get?.('pricingID') === savedPricing.pricingID
                    );
                pricing?.set('attributePricingID', savedPricing.attributePricingID);
            }
            for (const savedOption of savedAttribute.options ?? []) {
                if (!savedOption) continue;
                const option = findBy(attribute?.get('options'), 'optionGuid', savedOption.optionGuid);
                option?.set('optionID', savedOption.optionID);
                option?.set('tempImageFileKey', savedOption.tempImageFileKey ?? '');
                option?.set('tempImageUrl', savedOption.tempImageUrl ?? '');
                option?.set('imageUrl', savedOption.imageUrl ?? '');
                option?.set('tempImageFileObjectID', savedOption.tempImageFileObjectID ?? -1);
                option?.set('imageFileObjectID', savedOption.imageFileObjectID ?? -1);
                reconcileActions(option?.get('actions'), savedOption.actions);
            }
        }
        (saved.colors ?? []).forEach((savedColor, colorIndex) => {
            if (!savedColor) return;
            const colors = product.get('colors');
            const color =
                findBy(colors, 'colorID', savedColor.colorID) ??
                (savedColor.colorGuid && savedColor.colorGuid !== '00000000-0000-0000-0000-000000000000'
                    ? findBy(colors, 'colorGuid', savedColor.colorGuid)
                    : null) ??
                colors?.at?.(colorIndex);
            color?.set('colorID', savedColor.colorID);
            (savedColor.sides ?? []).forEach((savedSide, sideIndex) => {
                if (!savedSide) return;
                const sides = color?.get('sides');
                // Side GUIDs are correlation-only in some legacy products and may not be
                // returned after persistence, so use the stable response order as final fallback.
                const side =
                    (savedSide.sideGuid ? findBy(sides, 'sideGuid', savedSide.sideGuid) : null) ??
                    findBy(sides, 'sideID', savedSide.sideID) ??
                    sides?.at?.(sideIndex);
                side?.set('sideID', savedSide.sideID);
                side?.set('tempImageFileKey', savedSide.tempImageFileKey ?? '');
                side?.set('imageUrl', savedSide.imageUrl ?? '');
                (savedSide.areas ?? []).forEach((savedArea, areaIndex) => {
                    if (!savedArea) return;
                    const areas = side?.get('areas');
                    (findBy(areas, 'guid', savedArea.guid) ?? areas?.at?.(areaIndex))?.set('areaID', savedArea.areaID);
                });
            });
        });
    };

    const saveProductOnly = async (overrides: { productInfo?: { name: string; code: string; environment: number } } = {}) => {
        // The viewer supplies a default color without a GUID for new Composer products.
        // Once it owns print areas it must be persisted, with a stable identity across retries.
        zkProduct?.get('colors').forEach((color) => {
            const identity = color as unknown as {
                get(attribute: 'colorGuid'): string | null | undefined;
                set(attribute: 'colorGuid', value: string): void;
            };
            if (!identity.get('colorGuid') && color.get('sides').length > 0) {
                identity.set('colorGuid', MPlaza.generateUUID());
            }
        });
        const snapshot = zkProduct?.toJSON() as NonNullable<loadProductWithProductQuery$data['model']> | undefined;
        if (!snapshot) {
            throw new Error('Missing product data');
        }

        const freshProduct = !snapshot.modelID || snapshot.modelID === 0;
        const gqlId = freshProduct ? null : toGlobalID(snapshot.modelID, 'Product');

        const attributes = serializeAttributes(snapshot.attributes ?? []);
        const payload: ModelUpdateOrCreateInput = {
            id: gqlId,
            name: { value: overrides.productInfo?.name ?? zkProduct?.get('name') ?? 'New Product' },
            code: { value: overrides.productInfo?.code ?? zkProduct?.get('code') ?? `PROD_${Date.now()}` },
            environment: overrides.productInfo?.environment ? { value: overrides.productInfo.environment } : undefined,
            pricingModelID: freshProduct ? { value: 1 } : undefined,
            salesChannelID: {
                value: freshProduct ? toGlobalID('1', 'SalesChannel') : snapshot.salesChannelID
            },
            scenes: { value: snapshot.scenes.map(buildScenePayload) },
            preview3D: { value: mapPreviewVariants(snapshot.preview3D) },
            colors: { value: serializeColors((snapshot.colors ?? []).filter((c) => !!c?.colorGuid)) },
            attributes: {
                value: attributes
            },
            attributeSteps: { value: serializeAttributesStep(snapshot.attributeSteps ?? []) },
            attributeGroups: { value: serializeAttributesGroup(snapshot.attributeGroups ?? []) },
            templateGroups: { value: serializeTemplateGroups(snapshot.templateGroups ?? []) },
            cameraSideMappings: { value: serializeCameraSideMappings(snapshot.colors ?? []) }
        };

        const serializedInput = JSON.stringify(payload);
        const compressed = compressToBase64(serializedInput);

        const savedProduct = await withTimeout(
            new Promise<useSaveProductUpdateProductOnlyMutation$data>((resolve, reject) => {
                updateProduct({
                    variables: config.enableCompressionForSavingReadingProducts ? { compressedInput: compressed } : { input: payload },
                    onCompleted: (response, errors) => {
                        if (errors?.length) {
                            reject(new Error(errors.map((error) => error.message).join(', ')));
                            return;
                        }
                        resolve(response);
                    },
                    onError: (error) => reject(error)
                });
            }),
            SAVE_OPERATION_TIMEOUT_MS,
            T._('The product save took too long. Please try again.', 'Admin')
        );

        const savedModel = savedProduct.createOrUpdateModelV2.model;
        if (!savedModel) throw new Error(T._('The server did not return the saved product. Please try again.', 'Admin'));
        reconcileSavedProduct(zkProduct!, savedModel);

        // incrementSaveVersion();
    };

    return [saveProductOnly, isSavingProduct] as const;
};

// Save the scene with visibility metadata optimization (used by the composer flow).
// Snapshots currently visible mesh IDs into scene.metadata.visibleMeshesID, hides every
// mesh for a clean "all hidden" .babylon baseline, exports the scene, then restores the
// live view so the canvas doesn't appear blank.
const useSaveOptimizedScene = () => {
    const [zkProduct, zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkProduct, store.zkScene, store.sceneEditor]);
    const [isExportingScene, setIsExportingScene] = useState(false);

    const [createOrUpdateScene, isSavingScene] = useMutation<useSaveProductOptimizedSceneMutation>(graphql`
        mutation useSaveProductOptimizedSceneMutation($input: SceneInput!) {
            updateScene(input: $input) {
                scene {
                    id
                }
            }
        }
    `);

    const saveOptimizedScene = async (onProgress?: (progress: number) => void) => {
        if (!sceneEditor || !zkScene || !zkProduct || typeof sceneEditor.updateSceneModel !== 'function') {
            throw new Error(T._('The 3D scene is not ready to be saved. Wait for it to finish loading and try again.', 'Admin'));
        }

        const { EditingMode } = await import('@zakeke/zakeke-3d-core');

        sceneEditor.setEditingMode(EditingMode.None);
        sceneEditor.showHideEditControl(false);

        (sceneEditor as any).saveVisibleMeshesToMetadata?.();

        setIsExportingScene(true);
        try {
            await withTimeout(
                new Promise<void>((resolve, reject) => {
                    sceneEditor.updateSceneModel(
                        () => resolve(),
                        (error?: unknown) => reject(error instanceof Error ? error : new Error(T._('Failed to export the 3D scene.', 'Admin'))),
                        onProgress
                    );
                }),
                SAVE_OPERATION_TIMEOUT_MS,
                T._('The 3D scene export took too long. Please try again.', 'Admin')
            );
        } finally {
            setIsExportingScene(false);
            (sceneEditor as any).applyVisibleMeshesFromMetadata?.();
        }

        if (!sceneEditor.zkScene) {
            throw new Error(T._('The exported 3D scene is not available. Please try again.', 'Admin'));
        }
        const sceneJson = cloneDeep(sceneEditor.zkScene.toJSON());

        if (sceneJson.config?.general?.tryOnSettings?.type !== undefined) {
            const originalType = sceneJson.config.general.tryOnSettings.type;
            const convertedType = tryOnGraphqlMap.get(originalType);
            if (convertedType) {
                sceneJson.config.general.tryOnSettings.type = convertedType;
            } else {
                delete sceneJson.config.general.tryOnSettings;
            }
        }

        const saveSceneResult = await withTimeout(
            new Promise<useSaveProductOptimizedSceneMutation$data>((resolve, reject) => {
                createOrUpdateScene({
                    variables: {
                        input: {
                            ...sceneJson,
                            id: toGlobalID(zkScene.get('sceneID') || zkScene.get('tempSceneID'), 'Scene'),
                            tempSceneID: undefined,
                            sceneID: undefined,
                            sceneIDforPagination: undefined
                        }
                    },
                    onCompleted: (response, errors) => {
                        if (errors?.length) {
                            reject(new Error(errors.map((error) => error.message).join(', ')));
                            return;
                        }
                        resolve(response);
                    },
                    onError: (error) => reject(error)
                });
            }),
            SAVE_OPERATION_TIMEOUT_MS,
            T._('The 3D scene save took too long. Please try again.', 'Admin')
        );

        const savedSceneGlobalID = saveSceneResult.updateScene.scene?.id;
        if (!savedSceneGlobalID) {
            throw new Error(T._('The server did not return the saved 3D scene. Please try again.', 'Admin'));
        }
        const savedSceneID = toLocalGuid(savedSceneGlobalID);
        if (!savedSceneID) {
            throw new Error(T._('The saved 3D scene identifier is invalid. Please try again.', 'Admin'));
        }
        zkScene.set('sceneID', savedSceneID);
        zkProduct.get('scenes').at(0).set('sceneID', savedSceneID);
    };

    const isSaving = isExportingScene || isSavingScene;

    return [saveOptimizedScene, isSaving] as const;
};

// save product and scene
const useSaveProductAndScene = () => {
    const [zkProduct, zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkProduct, store.zkScene, store.sceneEditor]);
    const [isExportingScene, setIsExportingScene] = useState(false);

    /* Save product */
    const [updateProduct, isSavingProduct] = useMutation<useSaveProductUpdateProductMutation>(graphql`
        mutation useSaveProductUpdateProductMutation($input: ModelUpdateOrCreateInput, $compressedInput: String) {
            createOrUpdateModelV2(input: $input, compressedInput: $compressedInput) {
                model {
                    id
                    salesChannelID
                }
            }
        }
    `);

    /* Save scene */
    const [createOrUpdateScene, isSavingScene] = useMutation<useSaveProductCreateOrUpdateSceneMutation>(graphql`
        mutation useSaveProductCreateOrUpdateSceneMutation($input: SceneInput!) {
            updateScene(input: $input) {
                scene {
                    id
                }
            }
        }
    `);

    /* Save VTO */
    const [createOrUpdateVTO] = useMutation<useSaveProductCreateOrUpdateVTOMutation>(graphql`
        mutation useSaveProductCreateOrUpdateVTOMutation($input: ProductTryOnInput!) {
            updateOrCreateTryOnProduct(input: $input) {
                productTryOn {
                    id
                }
            }
        }
    `);

    /* Disable VTO */
    const [disableProduct, isDisablingVTO] = useMutation<useSaveProductDisableVTOMutation>(graphql`
        mutation useSaveProductDisableVTOMutation($id: ID!) {
            disableTryOnProduct(id: $id) {
                productTryOn {
                    id
                }
            }
        }
    `);

    const saveProduct = async (
        settings: {
            onProgress?: (progress: number) => void;
            fromSceneEditor?: boolean;
            /** `salesChannelID` places a new product; without it a new product lands on sales channel 1. */
            productInfo?: { name: string; code: string; environment: number; salesChannelID?: string };
        } = {}
    ) => {
        const { EditingMode } = await import('@zakeke/zakeke-3d-core');

        // Disable the edit mode to avoid axis to be saved with the scene.
        sceneEditor?.setEditingMode(EditingMode.None);
        sceneEditor!.showHideEditControl(false);

        setIsExportingScene(true);
        await new Promise<void>((resolve, reject) => {
            sceneEditor?.updateSceneModel(
                () => resolve(),
                () => reject(),
                settings.onProgress
            );
        });
        setIsExportingScene(false);

        const sceneJson = cloneDeep(sceneEditor!.zkScene!.toJSON());

        // Convert number to graphql type
        if (sceneJson.config?.general?.tryOnSettings?.type !== undefined) {
            const originalType = sceneJson.config.general.tryOnSettings.type;
            const convertedType = tryOnGraphqlMap.get(originalType);
            if (convertedType) {
                sceneJson.config.general.tryOnSettings.type = convertedType;
            } else {
                console.warn('Failed to convert VTO type, removing tryOnSettings from scene');
                // Se non riusciamo a convertire il tipo, rimuoviamo le impostazioni VTO dalla scena
                delete sceneJson.config.general.tryOnSettings;
            }
        }

        const saveSceneResult = await new Promise<useSaveProductCreateOrUpdateSceneMutation$data>((resolve, reject) => {
            createOrUpdateScene({
                variables: {
                    input: {
                        ...sceneJson,
                        id: toGlobalID(zkScene!.get('sceneID') || zkScene!.get('tempSceneID'), 'Scene'),
                        tempSceneID: undefined, // use graphql id
                        sceneID: undefined, // use graphql id
                        sceneIDforPagination: undefined // use graphql id
                    }
                },
                onCompleted: (res) => {
                    resolve(res);
                },
                onError: () => {
                    reject();
                }
            });
        });

        zkScene!.set('sceneID', toLocalGuid(saveSceneResult.updateScene.scene!.id)!);
        zkProduct!.get('scenes').at(0).set('sceneID', toLocalGuid(saveSceneResult.updateScene.scene!.id)!);

        const product = zkProduct!.toJSON() as NonNullable<loadProductWithProductQuery$data['model']>;

        // ✅ NUOVO: Determina se è un nuovo prodotto
        const isNewProduct = !product.modelID || product.modelID === 0;
        const productId = isNewProduct ? null : toGlobalID(product.modelID, 'Product');

        console.log('🔍 Save Product Debug Info:', {
            isNewProduct,
            originalModelID: product.modelID,
            productId,
            hasScenes: product.scenes?.length > 0,
            sceneID: product.scenes?.[0]?.sceneID,
            productName: settings.productInfo?.name || zkProduct!.get('name'),
            productCode: settings.productInfo?.code || zkProduct!.get('code'),
            environment: settings.productInfo?.environment || zkProduct!.get('environment'), // ✅ Usa productInfo se disponibile
            salesChannelID: product.salesChannelID
        });

        const mutationInputForUpdateProduct: ModelUpdateOrCreateInput = {
            id: productId,
            name: { value: settings.productInfo?.name || zkProduct!.get('name') || 'New Product' },
            code: { value: settings.productInfo?.code || zkProduct!.get('code') || `PROD_${Date.now()}` },
            environment: settings.productInfo?.environment ? { value: settings.productInfo.environment } : undefined,
            pricingModelID: isNewProduct ? { value: 1 } : undefined,
            salesChannelID: {
                value: isNewProduct ? (settings.productInfo?.salesChannelID ?? toGlobalID((1).toString(), 'SalesChannel')) : product.salesChannelID
            },
            scenes: {
                value: product.scenes.map((scene) => {
                    return {
                        sceneID: toLocalGuid(scene!.sceneID!),
                        isDefault: scene!.isDefault,
                        meshDesignEffects: [],
                        meshSides: scene!.meshSides.map((meshSide) => {
                            return {
                                sideID: meshSide.sideID,
                                colorID: meshSide.colorID,
                                attributeID: meshSide.attributeID ?? null,
                                config: {
                                    general: {
                                        desktopTextureResolutionHeight: meshSide.config?.general?.desktopTextureResolutionHeight ?? null,
                                        desktopTextureResolutionWidth: meshSide.config?.general?.desktopTextureResolutionWidth ?? null,
                                        mobileTextureResolutionHeight: meshSide.config?.general?.mobileTextureResolutionHeight ?? null,
                                        mobileTextureResolutionWidth: meshSide.config?.general?.mobileTextureResolutionWidth ?? null,
                                        showDesign: true
                                    },
                                    diffuse: {
                                        enabled: meshSide.config?.diffuse?.enabled ?? true,
                                        blendMode: meshSide.config?.diffuse?.blendMode ?? 'normal',
                                        level: meshSide.config?.diffuse?.level ?? 1
                                    }
                                },
                                meshID: meshSide.meshID,
                                matrix: meshSide.matrix,
                                transform: meshSide.transform,
                                isNormalmapEnabled: false,
                                percHeight: 1,
                                percWidth: 1,
                                subMeshID: 0,
                                uvChannel: 0,
                                materialID: meshSide.materialID
                            };
                        })
                    };
                })
            },
            preview3D: {
                value: product.preview3D
                    ? {
                          variantsAttributeOptions: {
                              attributeGuid: product.preview3D.variantsAttributeOptions!.attributeGuid,
                              variableMaterials: product.preview3D.variantsAttributeOptions!.variableMaterials!.map((variableMaterial) => {
                                  return {
                                      materialID: variableMaterial!.materialID,
                                      meshes: variableMaterial!.meshes
                                  };
                              }),
                              variantOptions: product.preview3D.variantsAttributeOptions!.variantOptions!.map((variantOption) => {
                                  return {
                                      variantID: variantOption!.variantID,
                                      optionGuid: variantOption!.optionGuid,
                                      meshes: variantOption!.meshes,
                                      materials: variantOption!.materials!.map((material) => {
                                          return {
                                              optionMaterialID: material!.optionMaterialID,
                                              variableMaterialID: material!.variableMaterialID,
                                              actions: material!.actions!.map((action) => {
                                                  return {
                                                      actionGuid: action!.actionGuid,
                                                      meshID: action!.meshID
                                                  };
                                              })
                                          };
                                      })
                                  };
                              })
                          }
                      }
                    : null
            },
            attributes: {
                value: product.attributes!.map((attribute) => {
                    return {
                        attributeID: attribute?.attributeID ?? null,
                        attributeGuid: attribute!.attributeGuid,
                        attributeCode: attribute!.attributeCode,
                        attributeTypeID: attribute!.attributeTypeID,
                        defaultValue: attribute!.defaultValue,
                        displayOrder: attribute!.displayOrder,
                        enabled: attribute!.enabled !== false,
                        hideOptionsLabel: false,
                        preActions: [],
                        postActions: [],
                        pricings: [],
                        valueActions: [],
                        name: attribute!.name,
                        description: attribute!.description,
                        groupID: attribute!.groupID,
                        groupGuid: attribute!.groupGuid,
                        stepID: attribute!.stepID,
                        stepGuid: attribute!.stepGuid,
                        optionPreviewShapeID: attribute!.optionPreviewShapeID,
                        options: attribute!.options!.map((option) => {
                            return {
                                optionID: option?.optionID ?? null,
                                optionGuid: option!.optionGuid,
                                optionCode: option!.optionCode,
                                tempImageFileKey: option!.tempImageFileKey,
                                imageUrl: option!.imageUrl,
                                name: option!.name,
                                description: option!.description,
                                displayOrder: option!.displayOrder,
                                enabled: option!.enabled !== false,
                                isDefault: option!.isDefault,
                                tempImageFileObjectID: option!.tempImageFileObjectID,
                                imageFileObjectID: option!.imageFileObjectID,
                                extensionFields: [],
                                actions: option!.actions!.map((action) => {
                                    return {
                                        actionID: action?.actionID ?? null,
                                        actionGuid: action!.actionGuid,
                                        name: action!.name,
                                        actionTypeID: action!.actionTypeID,
                                        sceneElementTypeID: action!.sceneElementTypeID,
                                        selector: action!.selector,
                                        propertyName: action!.propertyName,
                                        value: action!.value,
                                        execOrder: action!.execOrder,
                                        enabled: action!.enabled !== false,
                                        deviceTypes: action!.deviceTypes
                                    };
                                })
                            };
                        })
                    };
                })
            },
            cameraSideMappings: {
                value: serializeCameraSideMappings(product.colors)
            }
        };

        console.log({ product: mutationInputForUpdateProduct });
        console.log('🔍 Final Mutation Input Debug:', {
            hasId: 'id' in mutationInputForUpdateProduct,
            idValue: mutationInputForUpdateProduct.id,
            isNewProduct,
            useCompression: config.enableCompressionForSavingReadingProducts
        });

        // ✅ NUOVO: Log del JSON prima della compressione
        const jsonBeforeCompression = JSON.stringify(mutationInputForUpdateProduct);
        console.log('🔍 JSON Before Compression:', jsonBeforeCompression);
        console.log('🔍 JSON Parsed Back:', JSON.parse(jsonBeforeCompression));

        let compressedMutationInput = compressToBase64(jsonBeforeCompression);

        await new Promise<void>((resolve) => {
            updateProduct({
                variables: config.enableCompressionForSavingReadingProducts
                    ? {
                          compressedInput: compressedMutationInput
                      }
                    : {
                          input: mutationInputForUpdateProduct
                      },
                onCompleted: (res) => {
                    resolve();
                }
            });
        });

        const isTryOnEnabled = zkScene!.getTryOnEnabled() ?? false;
        const tryonSettings = zkScene!.getTryOnSettings(); // They have all the same info of the microservice, so I can use it as a state.

        console.log('🔍 VTO Save Debug:', {
            isTryOnEnabled,
            tryonSettings,
            originalType: tryonSettings?.type,
            originalTypeType: typeof tryonSettings?.type,
            isValidNumber: Number.isInteger(tryonSettings?.type),
            tryOnGraphqlMapEntries: Array.from(tryOnGraphqlMap.entries())
        });

        if (isTryOnEnabled && tryonSettings && !isNewProduct) {
            console.log('Updating VTO configuration...');

            // 🔧 DEBUG: Controlla la conversione del tipo
            const originalType = tryonSettings.type;

            // 🔧 FIX: Controlla se il tipo è NotSelected (0) e impedisci il salvataggio
            if (originalType === 0) {
                // TryOnType.NotSelected
                console.error('❌ Cannot save VTO with NotSelected type:', {
                    originalType,
                    isTryOnEnabled,
                    tryonSettings
                });
                throw new Error('VTO is enabled but no type is selected. Please select a VTO type before saving.');
            }

            let convertedTypeID = tryOnGraphqlMap.get(originalType);

            // 🔧 FIX: Se la conversione fallisce, prova a convertire da stringa a numero
            if (!convertedTypeID && typeof originalType === 'string') {
                console.log('🔄 Trying to parse string type to number:', originalType);
                const parsedType = parseInt(originalType, 10);
                if (!isNaN(parsedType)) {
                    convertedTypeID = tryOnGraphqlMap.get(parsedType);
                    console.log('🔄 Parsed type conversion result:', { parsedType, convertedTypeID });
                }
            }

            // 🔧 FIX: Se ancora fallisce, prova a fare il match diretto con le stringhe GraphQL
            if (!convertedTypeID && typeof originalType === 'string') {
                const directStringMatch = (originalType as string).toUpperCase();
                const possibleValues = Array.from(tryOnGraphqlMap.values());
                if (possibleValues.includes(directStringMatch as any)) {
                    convertedTypeID = directStringMatch as any;
                    console.log('🔄 Direct string match found:', directStringMatch);
                }
            }

            console.log('🔄 VTO Type Conversion:', {
                originalType,
                convertedTypeID,
                isValidConversion: convertedTypeID !== undefined
            });

            if (!convertedTypeID) {
                console.error('❌ Failed to convert VTO type for GraphQL:', {
                    originalType,
                    availableTypes: Array.from(tryOnGraphqlMap.keys()),
                    availableGraphQLValues: Array.from(tryOnGraphqlMap.values()),
                    tryOnGraphqlMap: Object.fromEntries(tryOnGraphqlMap)
                });
                throw new Error(`Invalid VTO type conversion: ${originalType} (type: ${typeof originalType})`);
            }

            await new Promise<void>((resolve, reject) => {
                const vtoInput = {
                    id: productId!, // Non-null assertion perché sappiamo che !isNewProduct
                    productName: zkProduct!.get('name'),
                    productCode: zkProduct!.get('code'),
                    typeID: convertedTypeID as TryOnType,
                    settings: {
                        pd: tryonSettings.typeSettings.pd ?? false,
                        tryOn: tryonSettings.typeSettings.try_on ?? false,
                        active: tryonSettings.typeSettings.active ?? false
                    },
                    // 🔧 CORRETTO: Usa il campo mandatoryPD separato se disponibile, altrimenti fallback a pd per retrocompatibilità
                    mandatoryPD: (tryonSettings.typeSettings as any).mandatoryPD ?? tryonSettings.typeSettings.pd ?? false
                };

                console.log(' VTO TypeSettings Detail:', {
                    typeSettings: tryonSettings.typeSettings,
                    mandatoryPDFromTypeSettings: (tryonSettings.typeSettings as any).mandatoryPD,
                    pdFromTypeSettings: tryonSettings.typeSettings.pd,
                    finalMandatoryPDValue: (tryonSettings.typeSettings as any).mandatoryPD ?? tryonSettings.typeSettings.pd ?? false
                });

                console.log('📤 VTO Mutation Input:', vtoInput);

                createOrUpdateVTO({
                    variables: {
                        input: vtoInput
                    },
                    onCompleted: () => {
                        console.log('VTO configuration updated successfully');
                        resolve();
                    },
                    onError: (error) => {
                        console.error('Error updating VTO configuration:', error);
                        reject();
                    }
                });
            });
        } else if (tryonSettings && !isTryOnEnabled && !isNewProduct) {
            // Solo disabilita se il prodotto aveva precedentemente il VTO configurato E non è un prodotto nuovo
            console.log('Disabling VTO for product that had VTO previously...');
            await new Promise<void>((resolve, reject) => {
                disableProduct({
                    variables: {
                        id: productId! // Non-null assertion perché sappiamo che !isNewProduct
                    },
                    onCompleted: () => {
                        console.log('VTO disabled successfully');
                        resolve();
                    },
                    onError: (error) => {
                        console.error('Error disabling VTO:', error);
                        // Non fare reject per errori di disabilitazione VTO
                        // perché il prodotto potrebbe non avere mai avuto VTO configurato
                        console.log('Continuing save process despite VTO disable error...');
                        resolve();
                    }
                });
            });
        } else {
            console.log('No VTO configuration found or needed, skipping VTO operations');
        }
    };

    const isSaving = isExportingScene || isSavingProduct || isSavingScene || isDisablingVTO;

    return [saveProduct, isSaving] as const;
};

export default useSaveProductAndScene;

export { useSaveProduct, useSaveOptimizedScene };
